import { useCallback, useEffect, useRef, useState } from 'react'

interface UseTooltipStateProps {
	readonly closeDelay: number
	readonly onOpen: () => void
	readonly onClose: () => void
}

export const useTooltipState = ({
	closeDelay,
	onOpen,
	onClose,
}: UseTooltipStateProps) => {
	const [show, setShow] = useState(false)
	const [shouldDisplay, setShouldDisplay] = useState(false)
	const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

	const cancelCloseTimeout = useCallback(() => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current)
			closeTimeoutRef.current = null
		}
	}, [])

	const handleOpen = useCallback(() => {
		cancelCloseTimeout()
		setShow(true)
		setShouldDisplay(true)
		onOpen()
	}, [cancelCloseTimeout, onOpen])

	const handleClose = useCallback(() => {
		cancelCloseTimeout()
		closeTimeoutRef.current = setTimeout(() => {
			setShouldDisplay(false)
			onClose()
		}, closeDelay)
	}, [cancelCloseTimeout, closeDelay, onClose])

	const handleCloseImmediate = useCallback(() => {
		cancelCloseTimeout()
		setShouldDisplay(false)
		onClose()
	}, [cancelCloseTimeout, onClose])

	const handleCloseAnimation = useCallback(() => {
		if (!shouldDisplay) {
			setShow(false)
		}
	}, [shouldDisplay])

	useEffect(
		() => () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current)
			}
		},
		[]
	)

	useEffect(() => {
		const handleEscapeKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				handleCloseImmediate()
			}
		}

		window.addEventListener('keydown', handleEscapeKey)
		return () => {
			window.removeEventListener('keydown', handleEscapeKey)
		}
	}, [handleCloseImmediate])

	return {
		show,
		shouldDisplay,
		handleOpen,
		handleClose,
		handleCloseImmediate,
		handleCloseAnimation,
		cancelCloseTimeout,
	}
}
